<script lang="ts" setup>
import { computed, ref } from 'vue'
import { flyCoins } from '../composables/coinFly'
import { useCoinDisplayHold } from '../composables/useCoinDisplayHold'
import DiamondCardReward from './modules/DiamondCardReward.vue'
import WelfareGuideAddScreen from './modules/WelfareGuideAddScreen.vue'
import WelfareGuideOpen from './modules/WelfareGuideOpen.vue'

interface WelfareTexts {
  myWallet: string
  coins: string
  dailyCheckIn: string
  youHaveCheckedIn: string
  dayStraight: string
  checkIn: string
  tomorrowToGetMore: string
  tomorrowCheckIn: string
  tasks: string
  checkInGet: string
  get: string
  ok: string
  go: string
  day: (day: number) => string
  guideAddStep1: string
  guideAddAtTopRight: string
  guideAddAtBottom: string
  guideAddAtBottomRight: string
  guideAddShare: string
  guideAddToScreen: string
  guideOpenTitle: string
  guideOpenAction: string
}

const props = defineProps<{
  config: {
    theme: {
      btnBg: string
      btnText: string
      coinsColor: string
    }
    resources: {
      welfareGold: string
      welfareBox: string
    }
    texts: WelfareTexts
  }
  homeBgUrl: string
  signed: boolean
  progress: any[]
  modalSigned: boolean
  progressInfo: any
  tomorrowInfo: any
  checkInDayNum: number
  showTabbar: boolean
  tasks: any[]
  showGuidePopupAddHome: boolean
  showGuideOpen: boolean
  finishPopup: boolean
  coins: number
  completeTaskDescription: string
  userCoinBalance: number
  getBtnStatus: (item: any) => string
  onCheckIn: () => void
  handleTask: (item: any) => void
  onCheckInOk: () => void
}>()

const emit = defineEmits<{
  (e: 'update:modalSigned', value: boolean): void
  (e: 'update:showGuidePopupAddHome', value: boolean): void
  (e: 'update:showGuideOpen', value: boolean): void
  (e: 'update:finishPopup', value: boolean): void
}>()

// 今日待签格：未签到时为已签天数的下一格
const todayIndex = computed(() => (props.signed ? -1 : props.checkInDayNum))

// #region 签到成功：钻石飞向顶部余额
const bigDiamondRef = ref<HTMLImageElement>()
const flying = ref(false)
const { release: releaseCoinDisplay } = useCoinDisplayHold()

async function onClaim() {
  if (flying.value)
    return
  const target = document.querySelector<HTMLElement>('[data-coin-target]')
  try {
    if (bigDiamondRef.value && target) {
      flying.value = true
      await flyCoins({
        from: bigDiamondRef.value,
        to: target,
        icon: '/images/vowpath/welfare/diamond-big.png',
      })
    }
  }
  finally {
    // 动画结束（或无法播放）后才让顶部余额显示最新值
    releaseCoinDisplay()
    flying.value = false
    props.onCheckInOk()
  }
}
// #endregion
</script>

<template>
  <div class="min-h-100dvh pb-90px bg-n-bg-global">
    <!-- 品牌行：Logo + 钻石胶囊 -->
    <div class="brand-row flex items-center justify-between px-15px">
      <img src="/images/vowpath/home/logo.png" class="brand-logo" alt="Vowpath">
      <CoinEntry />
    </div>

    <!-- 签到卡 -->
    <div class="welfare-card mx-16px mt-6px px-14px pt-16px pb-14px">
      <h3 class="text-18px text-n-text-1 font-bold">
        {{ config.texts.dailyCheckIn }}
      </h3>
      <p class="text-12px mt-4px sub-text">
        {{ config.texts.youHaveCheckedIn }} {{ checkInDayNum }} {{ config.texts.dayStraight }}
      </p>
      <div class="mt-14px flex justify-between">
        <div
          v-for="(item, index) in progress"
          :key="item.num"
          class="text-center"
        >
          <div
            class="day-box flex flex-col items-center justify-center"
            :class="{ signed: item.isSign, today: index === todayIndex }"
          >
            <img v-if="index !== progress.length - 1" class="w-27px h-24px" src="/images/vowpath/home/icon-diamond.png">
            <img v-else class="w-27px h-27px" :src="config.resources.welfareBox">
            <span class="mt-2px text-10px text-white">+{{ item.prizeNum }}</span>
          </div>
          <div class="h-16px mt-8px flex justify-center items-center">
            <img v-if="item.isSign" src="/images/vowpath/welfare/check.png" class="w-13px h-9px">
            <span v-else class="text-10px" :class="index === todayIndex ? 'day-today' : 'day-normal'">{{ config.texts.day(item.num) }}</span>
          </div>
        </div>
      </div>
      <a
        v-if="progress.length"
        class="check-btn block mt-14px w-full text-15px text-white text-center"
        :class="{ 'check-btn--disabled': signed }"
        @click="onCheckIn"
      >
        <span v-if="!signed">{{ config.texts.checkIn }}</span>
        <span v-else-if="checkInDayNum > 0 && checkInDayNum === progress.length">{{ config.texts.tomorrowToGetMore }}</span>
        <span v-else>{{ config.texts.tomorrowCheckIn }} {{ tomorrowInfo?.prizeNum }}</span>
      </a>
    </div>

    <!-- Diamond Card 每日奖励领取（仅订阅用户，接口 eligible 控制显隐） -->
    <DiamondCardReward />

    <!-- 任务卡 -->
    <div v-if="tasks && tasks.length" class="welfare-card mt-10px mx-16px px-14px py-16px">
      <h3 class="text-18px text-n-text-1 font-bold">
        {{ config.texts.tasks }}
      </h3>

      <div
        v-for="(item, index) in tasks"
        :key="index"
        class="flex justify-between items-center py-16px px-2px task-row"
      >
        <div>
          <div class="text-n-text-1 font-medium">
            {{ item.taskTitle }}
          </div>
          <div class="text-12px flex items-center mt-4px">
            <img class="w-16px h-14px" src="/images/vowpath/home/icon-diamond.png">
            <span class="ml-1 task-coins">{{ `+${item.coins}` }}</span>
          </div>
        </div>

        <div
          v-if="item.buttonState !== 1"
          class="task-btn w-60px text-center py-4px text-12px text-white"
          @click="handleTask(item)"
        >
          {{ getBtnStatus(item) }}
        </div>
        <div v-else class="w-60px flex items-center justify-center">
          <img src="/images/vowpath/welfare/check.png" class="w-13px h-9px">
        </div>
      </div>
    </div>

    <WelfareGuideAddScreen
      :value="showGuidePopupAddHome"
      :texts="config.texts"
      @input="emit('update:showGuidePopupAddHome', $event)"
    />
    <WelfareGuideOpen
      :value="showGuideOpen"
      :texts="config.texts"
      @input="emit('update:showGuideOpen', $event)"
    />

    <!-- 签到成功：全屏庆祝层（丝带 + 光束 + 大钻石） -->
    <div v-if="modalSigned" class="congrats-mask" :class="{ 'is-flying': flying }" @click="onClaim">
      <img src="/images/vowpath/welfare/ribbon-congrats.png" class="congrats-ribbon">
      <div class="congrats-glow-wrap">
        <img src="/images/vowpath/welfare/glow.png" class="congrats-glow">
        <img ref="bigDiamondRef" src="/images/vowpath/welfare/diamond-big.png" class="congrats-diamond">
      </div>
      <p class="congrats-count">
        ×{{ progressInfo?.prizeNum }}
      </p>
    </div>

    <BasicModal :value="finishPopup" @input="emit('update:finishPopup', $event)">
      <div class="p-5">
        <div class="text-17px lh-2em font-bold text-center text-n-text-1">
          <p>{{ completeTaskDescription }} {{ config.texts.get }} <span class="text-n-primary">{{ coins }}</span> {{ config.texts.coins }}</p>
        </div>
        <a
          class="finish-btn block mt-4 w-full h-9 lh-9 text-15px text-white text-center rounded-full"
          @click="emit('update:finishPopup', false)"
        >
          {{ config.texts.ok }}
        </a>
      </div>
    </BasicModal>

    <BasicTabbar v-if="showTabbar" tab="rewards" />
  </div>
</template>

<style lang="scss" scoped>
.brand-row {
  height: 44px;
}

.brand-logo {
  height: 25px;
  width: auto;
}

/* 卡片：深底 + 描边（对照蓝湖 rgba(39,40,88) / rgba(66,66,109)） */
.welfare-card {
  background: rgba(39, 40, 88, 1);
  border: 1px solid rgba(66, 66, 109, 1);
  border-radius: 16px;
}

.sub-text {
  color: #8382a6;
}

/* 签到格：40x60 三态 */
.day-box {
  width: 40px;
  height: 60px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(134, 92, 160, 1);

  &.signed {
    background: rgba(40, 40, 90, 1);
    border-color: rgba(71, 47, 98, 1);

    img,
    span {
      opacity: 0.5;
    }
  }

  &.today {
    background: linear-gradient(180deg, rgba(142, 60, 122, 1) 0%, rgba(81, 54, 137, 1) 100%);
    border-color: rgba(255, 78, 147, 1);
  }
}

.day-normal {
  color: #8382a6;
}

.day-today {
  color: #ff4e93;
  font-weight: 600;
}

/* 签到按钮 */
.check-btn {
  height: 40px;
  line-height: 40px;
  border-radius: 20px;
  background: linear-gradient(270deg, rgba(255, 90, 205, 1) 0%, rgba(154, 87, 255, 1) 100%);
  font-weight: 600;

  &.check-btn--disabled {
    opacity: 0.35;
  }
}

/* 任务行 */
.task-row {
  border-bottom: 1px solid rgba(66, 66, 109, 1);

  &:last-child {
    border-bottom: none;
  }
}

.task-coins {
  color: #bb7afb;
}

.task-btn {
  border-radius: 15px;
  background: linear-gradient(270deg, rgba(255, 90, 205, 1) 0%, rgba(154, 87, 255, 1) 100%);
}

.finish-btn {
  background: linear-gradient(270deg, rgba(255, 90, 205, 1) 0%, rgba(154, 87, 255, 1) 100%);
}

/* 签到成功全屏庆祝层 */
.congrats-mask {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transition: background-color 0.4s ease;

  /* 钻石飞行中：遮罩变透明露出顶部余额，庆祝内容缩小淡出 */
  &.is-flying {
    background: rgba(0, 0, 0, 0);
    pointer-events: none;

    .congrats-ribbon,
    .congrats-glow-wrap,
    .congrats-count {
      opacity: 0;
      transform: scale(0.6);
      transition: opacity 0.35s ease, transform 0.35s ease;
    }
  }
}

.congrats-ribbon {
  width: 286px;
  height: auto;
}

.congrats-glow-wrap {
  position: relative;
  width: 214px;
  height: 213px;
  margin-top: 20px;
}

.congrats-glow {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  animation: congrats-spin 6s linear infinite;
}

.congrats-diamond {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 100px;
  height: 90px;
}

.congrats-count {
  margin-top: -30px;
  color: #ffffff;
  font-size: 24px;
  font-weight: bold;
  position: relative;
  z-index: 1;
}

@keyframes congrats-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
